import { GymTypes, Level } from 'fitbeat.models';
import { getDefaultExerciseVersion, isStaticFunctionalZone } from '../../config';
import { AudioSoundEvent, CardioOrResistance, Exercise, Phase,
    ScheduleEntryColourCode, SessionSequence, UserExerciseSession, WorkoutSettings } from '../../entities';
import { isBoxingWorkout } from '../workoutselection';
import { ISessionSequenceStrategy } from './isessionsequencestrategy';

export class Warmup implements ISessionSequenceStrategy {
    public getSessionSequence(
        exercise: Exercise,
        alernativeExercise: Exercise,
        secondAternativeExercise: Exercise,
        thirdAlternateExercise: Exercise,
        zone: number,
        quarter: number,
        level?: Level,
        workoutSettings?: WorkoutSettings,
        previousExerciseSessions?: UserExerciseSession[],
    ): SessionSequence[] {
        const isBoxing = isBoxingWorkout(previousExerciseSessions);

        const buildStartsIn = (duration: number) => ({
            durationInMilliseconds: duration,
            sequence: [{
                durationInMilliseconds: duration,
                videoFile: SessionSequence.preWarmupVideoName(),
                exerciseVersion: getDefaultExerciseVersion(),
                displayName: exercise.name,
                displayBurnOrBuildIcon: false,
                progressColourCode: ScheduleEntryColourCode.Rest,
                progressText: 'Warm up in {seconds}',
                showProcessedMetrics: false,
                showCalculatedMetrics: true,
                audioSoundEvent: undefined,
                tips: Exercise.getFormattedTips(exercise),
                phase: Phase.Rest,
                zone,
            }],
        });
        const buildWarmupStarted = (duration: number) => ({
            durationInMilliseconds: duration,
            sequence: [{
                durationInMilliseconds: duration,
                videoFile: exercise.uniqueReferenceNumber,
                exerciseVersion: exercise._rev,
                displayName: exercise.name,
                displayBurnOrBuildIcon: true,
                progressColourCode: ScheduleEntryColourCode.Play,
                progressText: 'Warmup started!',
                showProcessedMetrics: false,
                showCalculatedMetrics: true,
                audioSoundEvent: undefined,
                tips: Exercise.getFormattedTips(exercise),
                phase: Phase.Train,
                zone,
            }],
        });
        const buildWarmup = (duration: number) => ({
            durationInMilliseconds: duration,
            sequence: [{
                durationInMilliseconds: duration,
                videoFile: exercise.uniqueReferenceNumber,
                exerciseVersion: exercise._rev,
                displayName: exercise.name,
                displayBurnOrBuildIcon: true,
                progressColourCode: ScheduleEntryColourCode.Play,
                progressText: 'Warmup for {seconds}',
                showProcessedMetrics: false,
                showCalculatedMetrics: true,
                audioSoundEvent: AudioSoundEvent.startOfWorkout, // we want this beep time to match exercises
                tips: Exercise.getFormattedTips(exercise),
                phase: Phase.Train,
                zone,
            }],
        });
        const buildMoveToNextZone = (duration: number) => ({
            durationInMilliseconds: duration,
            sequence: [{
                durationInMilliseconds: duration,
                videoFile: SessionSequence.changeZonesVideo(),
                exerciseVersion: getDefaultExerciseVersion(),
                displayName: exercise.name,
                displayBurnOrBuildIcon: false,
                progressColourCode: ScheduleEntryColourCode.Rest,
                progressText: 'Move to zone {zoneNumber} now',
                showProcessedMetrics: false,
                showCalculatedMetrics: true,
                audioSoundEvent: AudioSoundEvent.endOfWorkoutSession,
                tips: Exercise.getFormattedTips(exercise),
                phase: Phase.Rest,
                zone,
            }],
        });
        const buildZoneEnd = (duration: number) => ({
            durationInMilliseconds: duration,
            sequence: [{
                durationInMilliseconds: duration,
                videoFile: SessionSequence.getPauseVideoFileName(
                    workoutSettings?.cardioOrResistance ?? CardioOrResistance.Resistance,
                    GymTypes.Indoor,
                    isBoxing,
                  ),
                exerciseVersion: getDefaultExerciseVersion(),
                displayName: exercise.name,
                displayBurnOrBuildIcon: false,
                progressColourCode: ScheduleEntryColourCode.Rest,
                progressText: 'Start in {seconds}',
                showProcessedMetrics: false,
                showCalculatedMetrics: true,
                audioSoundEvent: AudioSoundEvent.endOfWorkoutSession,
                tips: Exercise.getFormattedTips(exercise),
                phase: Phase.Rest,
                zone,
            }],
        });
        return [
          buildStartsIn(16),
          buildWarmupStarted(4),
          buildWarmup(96),
          isStaticFunctionalZone(zone) ? buildZoneEnd(4) : buildMoveToNextZone(4),
        ];
    }
}
